import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { doc, updateDoc, deleteDoc } from "firebase/firestore"; 
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function Orders() {
  // 🔥 Role add kiya (isAdmin, isAccount)
  const { orders, loading, isAdmin, isAccount } = useWebData();

  // 🔥 Filter States (New Logic)
  const [viewMode, setViewMode] = useState('Month'); 
  const [selectedDate, setSelectedDate] = useState(new Date()); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); 
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);
  
  // Modal States
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);

  // ==========================================
  // 1. UNIVERSAL FILTER LOGIC (Fixed)
  // ==========================================
  useEffect(() => {
    if (!orders) return;

    let result = [...orders];

    // A. Search Filter
    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        result = result.filter(i => {
            const dateStr = i.date ? new Date(i.date).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric'
            }).toLowerCase() : "";

            const row = `${i.hospitalName} ${i.poNumber} ${i.amount} ${i.status} ${i.productDetails} ${i.userName} ${i.city}`.toLowerCase();
            return row.includes(lower) || dateStr.includes(lower);
        });
    }

    // 🔥 B. Date Filter (Universal String Match)
    if (viewMode !== 'All') {
        const targetYear = viewMode === 'Year' ? parseInt(selectedYear) : selectedDate.getFullYear();
        const targetMonth = selectedDate.getMonth(); 

        result = result.filter(item => {
            const rawDate = item.date || ""; 
            if (!rawDate) return false;

            let y = 0; let m = -1;
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) { y = d.getFullYear(); m = d.getMonth(); } 
            else {
                const parts = rawDate.split(/[-/]/);
                if (parts.length === 3) {
                    if (parts[2].length === 4) { y = parseInt(parts[2]); m = parseInt(parts[1]) - 1; } 
                    else if (parts[0].length === 4) { y = parseInt(parts[0]); m = parseInt(parts[1]) - 1; }
                }
            }

            if (viewMode === 'Year') return y === targetYear;
            if (viewMode === 'Month') return y === targetYear && m === targetMonth;
            return true;
        });
    }

    // C. Sort
    result.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    setFiltered(result);
  }, [orders, searchTerm, viewMode, selectedDate, selectedYear]); 

  // ==========================================
  // 2. EXCEL DOWNLOAD
  // ==========================================
  const downloadExcel = () => {
    const dataToExport = filtered.map(i => ({
        Order_Date: formatDate(i.date),
        Hospital: i.hospitalName,
        City: i.city || '-',
        PO_Number: i.poNumber,
        Amount: i.amount,
        Product_Machine: i.productDetails,
        Status: i.status,
        
        // 🔥 Tracking Info in Excel
        Dispatch_Date: formatDate(i.dispatchDate),
        Delivery_Date: formatDate(i.deliveryDate),
        
        Payment_Terms: i.paymentTerms,
        Delivery_Terms: i.deliveryTerms,
        Engineer: i.userName,
        File_Link: i.poFileUri || 'No File'
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    XLSX.writeFile(wb, `Orders_Report_${viewMode}.xlsx`);
  };

  // --- ACTIONS ---
  
  // 🔥 UPDATED: Auto-save Dates (Dispatch/Delivery)
  const updateStatus = async (row, status) => {
      if(window.confirm(`Update Order Status to ${status}?`)) {
          try {
              const updatePayload = { status };
              const now = new Date().toISOString();

              if(status === 'Dispatched') updatePayload.dispatchDate = now;
              if(status === 'Delivered') updatePayload.deliveryDate = now;

              await updateDoc(doc(db, "orders", row.id), updatePayload);
              
              if(viewData && viewData.id === row.id) {
                  setViewData({...viewData, ...updatePayload});
              }
          } catch(e) { alert("Error updating status"); }
      }
  };

  const handleSaveEdit = async () => {
      if(!editData) return;
      try {
          await updateDoc(doc(db, "orders", editData.id), {
              amount: editData.amount,
              productDetails: editData.productDetails,
              status: editData.status,
              paymentTerms: editData.paymentTerms,
              deliveryTerms: editData.deliveryTerms,
              notes: editData.notes,
              // Editable Dates
              dispatchDate: editData.dispatchDate,
              deliveryDate: editData.deliveryDate
          });
          alert("Order Updated ✅");
          setEditData(null);
      } catch (e) { alert("Error"); }
  };

  const handleDelete = async (id) => {
      if(window.confirm("Delete this order?")) await deleteDoc(doc(db, "orders", id));
  };

  // Helpers
  const handleMonthChange = (dir) => {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + dir);
      setSelectedDate(d);
  };

  const getYearOptions = () => {
      const currentY = new Date().getFullYear();
      const years = [];
      for(let y = 2024; y <= currentY + 5; y++) years.push(y);
      return years;
  };

  const formatDate = (iso) => {
      if(!iso) return "-";
      const date = new Date(iso);
      return isNaN(date.getTime()) ? iso : date.toLocaleDateString('en-GB');
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", backgroundColor:"#f8f9fa", minHeight:"100vh"}}>
      
      {/* HEADER */}
      <div style={headerCard}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px"}}>
            <h2 style={{margin:0, color:"#e67e22", display:"flex", alignItems:"center"}}>
                🛒 Orders / PO <span style={{fontSize:"14px", color:"#777", marginLeft:"10px"}}>({filtered.length})</span>
            </h2>
            <button onClick={downloadExcel} style={btnDownload}>📥 Download Excel</button>
        </div>

        <div style={{display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between"}}>
            <div style={{display:"flex", gap:"5px", backgroundColor:"#eee", padding:"4px", borderRadius:"6px"}}>
                {['Month', 'Year', 'All'].map(mode => (
                    <button key={mode} onClick={()=>setViewMode(mode)} 
                        style={{...toggleBtn, backgroundColor: viewMode===mode ? "white" : "transparent", color: viewMode===mode ? "#333" : "#777", boxShadow: viewMode===mode ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}}>
                        {mode}
                    </button>
                ))}
            </div>

            {viewMode === 'Month' && (
                <div style={{display:"flex", alignItems:"center", gap:"10px", backgroundColor:"white", padding:"5px 15px", borderRadius:"6px", border:"1px solid #ddd"}}>
                    <button onClick={()=>handleMonthChange(-1)} style={navBtn}>◀</button>
                    <span style={{fontWeight:"bold", color:"#333", minWidth:"120px", textAlign:"center"}}>
                        {selectedDate.toLocaleDateString('en-US', {month:'long', year:'numeric'})}
                    </span>
                    <button onClick={()=>handleMonthChange(1)} style={navBtn}>▶</button>
                </div>
            )}

            {viewMode === 'Year' && (
                <select 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{padding:"8px", borderRadius:"6px", border:"1px solid #ddd", fontWeight:"bold", fontSize:"14px", cursor:"pointer"}}
                >
                    {getYearOptions().map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            )}

            <input type="text" placeholder="🔍 Search PO, Hospital..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={searchInput} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto", backgroundColor:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <table style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#e67e22", color:"white", textAlign:"left"}}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Hospital & City</th>
                    <th style={thStyle}>Product / Details</th>
                    <th style={thStyle}>PO & Amount</th>
                    <th style={thStyle}>Engineer</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                  <tr><td colSpan="7" style={{padding:"20px", textAlign:"center", color:"#999"}}>No orders found.</td></tr>
              ) : filtered.map(row => (
                <tr key={row.id} style={{borderBottom:"1px solid #eee", height:"45px"}}>
                    <td style={tdStyle}>{formatDate(row.date)}</td>
                    
                    <td style={{...tdStyle, textAlign:"left"}}>
                        <div style={{fontWeight:"bold", color:"#2c3e50"}}>{row.hospitalName}</div>
                        <div style={{fontSize:"11px", color:"#7f8c8d"}}>{row.city}</div>
                    </td>

                    <td style={{...tdStyle, maxWidth:"200px", fontSize:"11px", color:"#555"}} title={row.productDetails}>
                        {row.productDetails ? row.productDetails.substring(0, 50) + "..." : "-"}
                    </td>

                    <td style={tdStyle}>
                        <div style={{fontWeight:"bold"}}>{row.poNumber}</div>
                        <div style={{color:"green", fontWeight:"bold"}}>₹{row.amount}</div>
                    </td>

                    <td style={tdStyle}>{row.userName}</td>

                    <td style={tdStyle}>
                        <span style={{
                            padding:"4px 10px", borderRadius:"12px", fontSize:"11px", fontWeight:"bold",
                            backgroundColor: row.status==='Delivered'?'#e8f5e9':(row.status==='Approved'?'#e3f2fd':'#fff3e0'),
                            color: row.status==='Delivered'?'green':(row.status==='Approved'?'blue':'orange')
                        }}>
                            {row.status}
                        </span>
                    </td>
                    
                    <td style={tdStyle}>
                        <div style={{display:"flex", gap:"3px"}}>
                            {/* 🔥 STATUS BUTTONS */}
                            {(isAdmin || isAccount) && (
                                <>
                                    {row.status === 'Pending' && <button onClick={()=>updateStatus(row,'Approved')} style={btnApprove} title="Approve">✔</button>}
                                    {row.status === 'Approved' && <button onClick={()=>updateStatus(row,'Dispatched')} style={btnDispatch} title="Mark Dispatched">🚚</button>}
                                    {row.status === 'Dispatched' && <button onClick={()=>updateStatus(row,'Delivered')} style={btnDeliver} title="Mark Delivered">✅</button>}
                                </>
                            )}
                            <button onClick={() => setViewData(row)} style={btnIconEye} title="View">👁️</button>
                            {(isAdmin || isAccount) && (
                                <>
                                    <button onClick={() => setEditData(row)} style={btnIconEdit} title="Edit">✏️</button>
                                    <button onClick={() => handleDelete(row.id)} style={btnIconDelete} title="Delete">🗑️</button>
                                </>
                            )}
                        </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 🔥 VIEW MODAL (UPDATED) */}
      {viewData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>
                <h3 style={{margin:0, color:"#e67e22"}}>🛒 Order Details</h3>
                <button onClick={() => setViewData(null)} style={{background:"none", border:"none", fontSize:"18px", cursor:"pointer"}}>✖</button>
            </div>
            
            <div style={{fontSize:"13px", lineHeight:"1.6"}}>
                <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px"}}>
                    <div>
                        <p><strong>📅 Date:</strong> {formatDate(viewData.date)}</p>
                        <p><strong>🏥 Hospital:</strong> {viewData.hospitalName}</p>
                        <p><strong>📍 City:</strong> {viewData.city}</p>
                    </div>
                    <div>
                        <p><strong>🔢 PO No:</strong> {viewData.poNumber}</p>
                        <p><strong>💰 Amount:</strong> {viewData.amount}</p>
                        <p><strong>🔥 Status:</strong> {viewData.status}</p>
                    </div>
                </div>

                <div style={{background:"#fff3e0", padding:"10px", borderRadius:"5px", margin:"10px 0", borderLeft:"4px solid #e67e22"}}>
                    <p style={{margin:0}}><strong>📦 Product / Machine:</strong></p>
                    <p style={{marginTop:"5px", fontWeight:"bold"}}>{viewData.productDetails}</p>
                </div>

                {/* 🔥 TRACKING DATES SECTION */}
                {(viewData.dispatchDate || viewData.deliveryDate) && (
                    <div style={{background:"#f0f4ff", padding:"10px", borderRadius:"6px", margin:"10px 0"}}>
                        <h4 style={{margin:"0 0 5px 0", color:"#3498db", fontSize:"12px"}}>🚚 Tracking Info</h4>
                        {viewData.dispatchDate && <p style={{margin:0}}><strong>🚀 Dispatched On:</strong> {formatDate(viewData.dispatchDate)}</p>}
                        {viewData.deliveryDate && <p style={{margin:0}}><strong>✅ Delivered On:</strong> {formatDate(viewData.deliveryDate)}</p>}
                    </div>
                )}

                <p><strong>👤 Contact:</strong> {viewData.contactPerson} ({viewData.mobile})</p>
                <p><strong>💳 Payment:</strong> {viewData.paymentTerms}</p>
                <p><strong>🚚 Delivery Terms:</strong> {viewData.deliveryTerms}</p>
                <p><strong>👷 Engineer:</strong> {viewData.userName}</p>
                
                {viewData.poFileUri && (
                    <div style={{marginTop:"10px", padding:"8px", backgroundColor:"#f9f9f9", borderRadius:"5px", border:"1px dashed #ccc"}}>
                        <strong>📎 Attachment:</strong> <a href={viewData.poFileUri} target="_blank" rel="noreferrer" style={{color:"blue", textDecoration:"underline"}}>Open PO File</a>
                    </div>
                )}
            </div>
            <button onClick={() => setViewData(null)} style={btnClose}>Close</button>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{color:"#e67e22", marginTop:0}}>✏️ Edit Order</h3>
            
            <label style={labelStyle}>Product Details</label>
            <textarea value={editData.productDetails} onChange={(e)=>setEditData({...editData, productDetails:e.target.value})} style={{...modalInput, height:"60px"}}></textarea>

            <div style={{display:"flex", gap:"10px"}}>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Amount (₹)</label>
                    <input type="text" value={editData.amount} onChange={(e)=>setEditData({...editData, amount:e.target.value})} style={modalInput}/>
                </div>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Status</label>
                    <select value={editData.status} onChange={(e)=>setEditData({...editData, status:e.target.value})} style={modalInput}>
                        <option value="Pending">Pending</option>
                        <option value="Approved">Approved</option>
                        <option value="Dispatched">Dispatched</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Rejected">Rejected</option>
                    </select>
                </div>
            </div>

            {/* Manual Dates */}
            <div style={{display:"flex", gap:"10px"}}>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Dispatch Date</label>
                    <input type="date" value={editData.dispatchDate ? editData.dispatchDate.split('T')[0] : ''} onChange={(e)=>setEditData({...editData, dispatchDate:e.target.value})} style={modalInput}/>
                </div>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Delivery Date</label>
                    <input type="date" value={editData.deliveryDate ? editData.deliveryDate.split('T')[0] : ''} onChange={(e)=>setEditData({...editData, deliveryDate:e.target.value})} style={modalInput}/>
                </div>
            </div>

            <div style={{marginTop:"15px", textAlign:"right"}}>
                <button onClick={()=>setEditData(null)} style={btnCancel}>Cancel</button>
                <button onClick={handleSaveEdit} style={btnSave}>Update</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// STYLES
const headerCard={backgroundColor:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", marginBottom:"20px"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"12px 15px", borderBottom:"2px solid #eee", fontSize:"12px", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"10px 15px", verticalAlign:"middle"};
const toggleBtn={border:"none", padding:"6px 12px", borderRadius:"5px", cursor:"pointer", fontSize:"13px", fontWeight:"600", transition:"0.2s"};
const navBtn={background:"none", border:"none", cursor:"pointer", fontSize:"16px", color:"#555"};
const searchInput={padding:"8px 12px", border:"1px solid #ddd", borderRadius:"6px", width:"200px", outline:"none"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};

const btnApprove={backgroundColor:"#3498db", color:"white", border:"none", padding:"4px 8px", borderRadius:"4px", cursor:"pointer", fontSize:"11px", marginRight:"3px", fontWeight:"bold"};
const btnDispatch={backgroundColor:"#f39c12", color:"white", border:"none", padding:"4px 8px", borderRadius:"4px", cursor:"pointer", fontSize:"11px", marginRight:"3px", fontWeight:"bold"};
const btnDeliver={backgroundColor:"#27ae60", color:"white", border:"none", padding:"4px 8px", borderRadius:"4px", cursor:"pointer", fontSize:"11px", marginRight:"3px", fontWeight:"bold"};
const btnReject={backgroundColor:"#c0392b", color:"white", border:"none", padding:"4px 8px", borderRadius:"4px", cursor:"pointer", fontSize:"11px", marginRight:"3px", fontWeight:"bold"};

const btnIconEye={backgroundColor:"#e3f2fd", color:"#1976d2", border:"1px solid #bbdefb", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconEdit={backgroundColor:"#fff3e0", color:"#e67e22", border:"1px solid #ffe0b2", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#fff", color:"#777", border:"1px solid #ddd", padding:"5px 8px", borderRadius:"4px", cursor:"pointer"};

const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"12px", width:"450px", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 10px 30px rgba(0,0,0,0.2)", position:"relative"};
const modalInput={width:"100%", padding:"8px", margin:"5px 0", borderRadius:"4px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"13px"};
const labelStyle={fontSize:"11px", fontWeight:"bold", color:"#555"};
const btnSave={backgroundColor:"#e67e22", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginLeft:"5px"};
const btnCancel={backgroundColor:"#95a5a6", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer"};
const btnClose={marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"8px", borderRadius:"4px", width:"100%", cursor:"pointer"};

export default Orders;